import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import { Button, CircularProgress, Dialog, DialogContent, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import { API_SHARED_APP_LIST_APP_CONTENT_URL, API_SHARED_APP_LIST_CLEAR_CONTENTS_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { AppListContent, useSharedAppListWithContents } from '../../hooks/useSharedAppLists';
import { useSnackBar } from '../../hooks/useSnackBar';
import { convertToCSV, downloadCSV } from '../../util/csvExport';
import { deleteRequestJSON } from '../../util/services';
import AppListContentForm from './AppListContentForm';
import DeleteAppIdentifierConfirmation from './DeleteAppIdentifierConfirmation';

interface AppListContentsTableProps {
  sharedAppListId: string;
}

function AppListContentsTable({ sharedAppListId }: AppListContentsTableProps) {
  const { data, isLoading } = useSharedAppListWithContents(sharedAppListId);
  const appListContents = useMemo(() => data?.model?.appListContents || [], [data?.model?.appListContents]);
  const targetingProfiles = data?.model?.targetingProfiles || [];
  const { showSnackBar } = useSnackBar();
  const { apiHost } = useConfig();
  const queryClient = useQueryClient();
  const [editContent, setEditContent] = useState<AppListContent | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<AppListContent | null>(null);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  const deleteAppListContent = useMutation({
    mutationFn: (id: number) => deleteRequestJSON(
        apiHost,
        generatePath(API_SHARED_APP_LIST_APP_CONTENT_URL, { 
          sharedAppListId,
          id: id.toString()
        })
      ),
    onSuccess: (response) => {
      if (response.errors) {
        throw new Error(Array.isArray(response.errors) 
          ? response.errors.join(', ') 
          : response.errors);
      }
      
      queryClient.invalidateQueries({ queryKey: ['sharedAppListWithContents', sharedAppListId] });
    }
  });

  const clearAppListContents = useMutation({
    mutationFn: () => deleteRequestJSON(
        apiHost,
        generatePath(API_SHARED_APP_LIST_CLEAR_CONTENTS_URL, { 
          sharedAppListId
        })
      ),
    onSuccess: (response) => {
      if (response.errors) {
        throw new Error(Array.isArray(response.errors) 
          ? response.errors.join(', ') 
          : response.errors);
      }
      
      queryClient.invalidateQueries({ queryKey: ['sharedAppListWithContents', sharedAppListId] });
    }
  });

  const handleDeleteClick = (content: AppListContent) => {
    setContentToDelete(content);
  };

  const handleDeleteConfirm = async () => {
    if (!contentToDelete) return;
    
    try {
      await deleteAppListContent.mutateAsync(contentToDelete.id);
      showSnackBar('App identifier deleted successfully', 'success', {
        event: 'app_list_content_deleted',
        shared_app_list_id: sharedAppListId,
        content_id: contentToDelete.id,
      });
      setContentToDelete(null);
    } catch (error) {
      showSnackBar(`Error deleting app identifier: ${error.message}`, 'error', {
        event: 'app_list_content_delete_error',
        shared_app_list_id: sharedAppListId,
      });
    }
  };

  const handleDeleteCancel = () => {
    setContentToDelete(null);
  };

  const handleEditClick = (content: AppListContent) => {
    setEditContent(content);
    setShowAddForm(false);
  };

  const handleAddClick = () => {
    setEditContent(null);
    setShowAddForm(true);
  };

  const handleFormClose = () => {
    setEditContent(null);
    setShowAddForm(false);
  };

  const handleClearClick = () => {
    setShowClearConfirmation(true);
  };

  const handleClearConfirm = async () => {
    try {
      await clearAppListContents.mutateAsync();
      showSnackBar(`Successfully cleared all app identifiers from list`, 'success', {
        event: 'app_list_contents_cleared',
        shared_app_list_id: sharedAppListId,
      });
      setShowClearConfirmation(false);
    } catch (error) {
      showSnackBar(`Error clearing app identifiers: ${error.message}`, 'error', {
        event: 'app_list_contents_clear_error',
        shared_app_list_id: sharedAppListId,
      });
    }
  };

  const handleClearCancel = () => {
    setShowClearConfirmation(false);
  };

  const listName = useMemo(() => data?.model?.name || 'shared-app-list', [data?.model?.name]);

  const handleDownloadClick = useCallback(() => {
    try {
      if (!appListContents || appListContents.length === 0) {
        showSnackBar('No app identifiers to download', 'warning', {
          event: 'app_list_download_empty',
          shared_app_list_id: sharedAppListId,
        });
        return;
      }

      const csvHeaders = [
        { key: 'id' as keyof AppListContent, label: 'ID' },
        { key: 'appIdentifier' as keyof AppListContent, label: 'App Identifier' }
      ];

      const csvContent = convertToCSV(appListContents, csvHeaders);
      const sanitizedListName = listName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${sanitizedListName}_${timestamp}`;

      downloadCSV(csvContent, filename);
      showSnackBar('App list downloaded successfully', 'success', {
        event: 'app_list_downloaded',
        shared_app_list_id: sharedAppListId,
        count: appListContents.length,
      });
    } catch (error) {
      showSnackBar(`Error downloading app list: ${error.message}`, 'error', {
        event: 'app_list_download_error',
        shared_app_list_id: sharedAppListId,
      });
    }
  }, [appListContents, listName, sharedAppListId, showSnackBar]);

  return (
    <Paper sx={{ mt: 3, p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">App List Contents</Typography>
            <Typography variant="body1" color="text.secondary">
              ({appListContents.length} {appListContents.length === 1 ? 'item' : 'items'})
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button 
              variant="outlined" 
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadClick}
              disabled={appListContents.length === 0 || isLoading}
              type="button"
            >
              Download List
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleAddClick}
              disabled={showAddForm || !!editContent}
              type="button"
            >
              Edit List Contents
            </Button>
            <Button 
              variant="outlined" 
              color="error"
              onClick={handleClearClick}
              disabled={showAddForm || !!editContent || appListContents.length === 0 || clearAppListContents.isLoading}
              type="button"
            >
              Clear List
            </Button>
          </Stack>
        </Stack>

        <Dialog open={showAddForm || !!editContent} onClose={handleFormClose} maxWidth="sm" fullWidth>
          <DialogContent>
            {(showAddForm || editContent) && (
              <AppListContentForm 
                sharedAppListId={sharedAppListId}
                content={editContent}
                onClose={handleFormClose}
              />
            )}
          </DialogContent>
        </Dialog>

        {contentToDelete && (
          <DeleteAppIdentifierConfirmation
            open={!!contentToDelete}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            appIdentifier={contentToDelete.appIdentifier}
            targetingProfiles={targetingProfiles}
            isLoading={deleteAppListContent.isLoading}
          />
        )}

        <Dialog open={showClearConfirmation} onClose={handleClearCancel} maxWidth="sm" fullWidth>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="h6">
                Clear All App Identifiers?
              </Typography>
              <Typography variant="body1">
                Are you sure you want to remove all {appListContents.length} app identifier(s) from this list?
              </Typography>
              {targetingProfiles.length > 0 && (
                <Typography variant="body1" color="error">
                  Warning: This app list is currently in use by {targetingProfiles.length} AdGroup{targetingProfiles.length !== 1 ? 's' : ''}.
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ p: 2 }}>
            <Button onClick={handleClearCancel} disabled={clearAppListContents.isLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleClearConfirm} 
              color="error" 
              variant="contained" 
              disabled={clearAppListContents.isLoading}
            >
              {clearAppListContents.isLoading ? <CircularProgress size={24} /> : 'Clear All'}
            </Button>
          </Stack>
        </Dialog>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>App Identifier</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* eslint-disable-next-line no-nested-ternary */}
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : appListContents && appListContents.length > 0 ? (
                appListContents.map((content: AppListContent) => (
                  <TableRow key={content.id}>
                    <TableCell>{content.id}</TableCell>
                    <TableCell>{content.appIdentifier}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditClick(content);
                          }}
                          disabled={showAddForm || !!editContent}
                          type="button"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteClick(content);
                          }}
                          disabled={deleteAppListContent.isLoading}
                          color="error"
                          type="button"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No app identifiers found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}

export default AppListContentsTable; 